/**
 * "Matching accessories" upsell — same metafield/checkbox/cart mechanism as
 * complete-the-sofa-set.js (product.metafields.custom.matching_accessories,
 * one hidden items[][id]/items[][quantity] pair per checked card, variant
 * matched by title against the main product's currently selected variant),
 * plus two additions:
 *  - a per-card quantity stepper (design parity with the reference "Complete
 *    the look" addon strip) that feeds items[][quantity] instead of a fixed 1.
 *  - every custom option already on the main product — any hidden
 *    properties[Label] input already present on the product form (fabric,
 *    colour, seat firmness, chaise side, or any future custom-property
 *    block) — is mirrored onto each checked accessory as
 *    items[][properties][Label], so the accessory carries the same
 *    customisation as the sofa it's completing.
 *
 * Every accessory also gets items[][properties][<associated product label>]
 * naming the current sofa, so each accessory's cart line says which sofa it
 * belongs to. The bundle discount relies on this to apply per sofa.
 */

/** @type {Set<HTMLElement>} */
const matchingAccessoriesRoots = new Set();

/**
 * Fabric/colour-matched accessory images: `snippets/custom-fabric-color-options.liquid`
 * names each swatch's own image `{{ color_label | handle }}.png` (color_label =
 * `sofa_color_name`, falling back to `sofa_color`) and stamps that exact same
 * handle onto the swatch button as `data-swatch-slug`. Accessory images follow
 * the same slug with a suffix (e.g. "chenille-cream-pillow.jpg" for swatch
 * "chenille-cream.png"), so matching one is just: read the currently selected
 * swatch's `data-swatch-slug`, then check whether an accessory's image
 * filename contains it.
 *
 * Deliberately NOT read from `[data-selected-color-value]` (the display text
 * kept in sync by custom-fabric-color-options.js) — that span mirrors a
 * *different* field (`sofa_color`, no fabric-name fallback) than the one the
 * swatch image filename is actually built from, so for a colour where the
 * two metafields differ (e.g. display "Cream" vs image handle
 * "chenille-cream") the display text alone over-matches every other
 * material's "Cream" accessory photo too. Reading `data-swatch-slug` off the
 * selected button avoids reconstructing/guessing the slug entirely and stays
 * correct regardless of which of the two fields a given colour entry has set.
 *
 * Never falls back to a guessed/nonexistent URL — a card with no matching
 * image simply keeps its default product photo.
 * @returns {string}
 */
function getMaSwatchSlug() {
  const selected = document.querySelector(
    '.custom-fabric-color-options__swatch--selected[data-swatch-slug]'
  );
  return selected ? selected.getAttribute('data-swatch-slug') || '' : '';
}

/**
 * Slugifies a string the same way Shopify filenames are conventionally
 * written: lowercase, ASCII, words joined by single hyphens.
 * @param {string} str
 * @returns {string}
 */
function maSlugify(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Pulls the "line slug" out of an accessory image filename, e.g.
 * ".../aaron-pillow.jpg" -> "aaron", ".../mh-luxury-chesterfield-bolster-2.jpg"
 * -> "mh-luxury-chesterfield". Strips extension, query string, and a
 * trailing -pillow/-bolster (optionally -2, -3, …) suffix.
 * @param {string} src
 * @returns {string}
 */
function maExtractLineSlug(src) {
  let path = src;
  try {
    path = new URL(src, window.location.origin).pathname;
  } catch {
    /* src was already a bare path */
  }
  const filename = path.split('/').pop() || '';
  const withoutExt = filename.replace(/\.[a-z0-9]+$/i, '');
  return withoutExt.replace(/-(pillow|bolster)(-\d+)?$/i, '').toLowerCase();
}

/**
 * Finds, among an accessory's images, the one whose filename's line slug
 * (see maExtractLineSlug) matches the current sofa's product title — e.g. on
 * an "Aaron 3 Seater Sofa" page this picks the Matching Pillow/Bolster
 * product's "aaron-pillow.jpg" / "aaron-bolster.jpg" over any other line's
 * photo. Matches by substring (not just prefix) against the slugified title,
 * since some titles don't start with the line name verbatim (e.g. "Hartwell
 * Convertible U Shape Sofa" vs handle "m-h-convertible-u-shape-sofa", or
 * "Westmere Home Cinema Sofa" for the "cinema" line). When more than one line
 * slug matches (e.g. "mh-luxury" is also a substring of
 * "mh-luxury-chesterfield"), the longest — most specific — slug wins.
 * @param {string[]} images
 * @param {string} productTitleSlug
 * @returns {string | null}
 */
function maFindBestNameMatch(images, productTitleSlug) {
  if (!productTitleSlug) return null;
  let best = null;
  let bestLen = 0;
  for (const src of images) {
    const lineSlug = maExtractLineSlug(src);
    if (!lineSlug) continue;
    if (productTitleSlug.includes(lineSlug) && lineSlug.length > bestLen) {
      best = src;
      bestLen = lineSlug.length;
    }
  }
  return best;
}

document.addEventListener('cffco:selection', () => {
  matchingAccessoriesRoots.forEach((root) => {
    if (!document.body.contains(root)) {
      matchingAccessoriesRoots.delete(root);
      return;
    }
    const fn = /** @type {{ __maApplyImages?: () => void }} */ (root).__maApplyImages;
    fn?.();
  });
});

const MA_DRAG_THRESHOLD = 6;

/**
 * Horizontal strip: mouse drag-to-scroll (same pattern as the Google reviews /
 * customer gallery sliders in newcustom.js) plus Shift + mouse wheel.
 * Returns a function reporting whether the pointer moved past the drag
 * threshold during the most recent press, so callers can tell a drag apart
 * from a plain click on a card.
 * @param {HTMLElement | null} el
 * @returns {() => boolean}
 */
function wireMaTrackInteractions(el) {
  if (!(el instanceof HTMLElement)) return () => false;

  el.addEventListener(
    'wheel',
    (e) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      if (!e.shiftKey) return;
      const dy = e.deltaY;
      const dx = e.deltaX;
      const delta = Math.abs(dy) >= Math.abs(dx) ? dy : dx;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    },
    { passive: false }
  );

  let isDown = false;
  let startX = 0;
  let startScroll = 0;
  let dragMoved = false;

  el.addEventListener('mousedown', (e) => {
    isDown = true;
    dragMoved = false;
    el.classList.add('is-dragging');
    startX = e.pageX - el.offsetLeft;
    startScroll = el.scrollLeft;
  });
  el.addEventListener('mouseleave', () => {
    isDown = false;
    el.classList.remove('is-dragging');
  });
  el.addEventListener('mouseup', () => {
    isDown = false;
    el.classList.remove('is-dragging');
  });
  el.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const walk = e.pageX - el.offsetLeft - startX;
    if (Math.abs(walk) > MA_DRAG_THRESHOLD) dragMoved = true;
    el.scrollLeft = startScroll - walk;
  });

  let tStartX = 0;
  let tStartY = 0;
  el.addEventListener(
    'touchstart',
    (e) => {
      dragMoved = false;
      tStartX = e.touches[0].pageX;
      tStartY = e.touches[0].pageY;
    },
    { passive: true }
  );
  el.addEventListener(
    'touchmove',
    (e) => {
      const t = e.touches[0];
      if (Math.abs(t.pageX - tStartX) > MA_DRAG_THRESHOLD || Math.abs(t.pageY - tStartY) > MA_DRAG_THRESHOLD) {
        dragMoved = true;
      }
    },
    { passive: true }
  );

  // Swallow the click that immediately follows a drag/swipe before it reaches
  // the bubble-phase whole-card toggle click handler below.
  el.addEventListener(
    'click',
    (e) => {
      if (dragMoved) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    true
  );

  return () => dragMoved;
}

document.addEventListener('variant:update', () => {
  matchingAccessoriesRoots.forEach((root) => {
    if (!document.body.contains(root)) {
      matchingAccessoriesRoots.delete(root);
      return;
    }
    const fn = /** @type {{ __maSync?: () => void }} */ (root).__maSync;
    fn?.();
  });
});

// Belt-and-braces resync right before the main form is actually submitted, so
// any custom option changed after a checkbox was ticked (fabric, colour,
// seat firmness, …) is still captured. Registered on `window`, not
// `document`: Shopify Horizon's own submit handling (assets/component.js) is
// ALSO `document.addEventListener('submit', ..., {capture:true})`, loaded as
// a core module before this deferred block script, so a same-node listener
// here would always lose that registration-order race and run too late —
// `window` sits earlier than `document` in the capturing path regardless of
// registration order, guaranteeing this runs first every time.
window.addEventListener(
  'submit',
  (e) => {
    if (!(e.target instanceof HTMLFormElement)) return;
    const form = e.target;
    matchingAccessoriesRoots.forEach((root) => {
      if (!document.body.contains(root)) {
        matchingAccessoriesRoots.delete(root);
        return;
      }
      if (root.dataset.maFormId !== form.id) return;
      const fn = /** @type {{ __maSync?: () => void }} */ (root).__maSync;
      fn?.();
    });
  },
  true
);

/**
 * @param {HTMLElement} root
 */
function initMatchingAccessories(root) {
  if (root.dataset.maInitialized === 'true') return;
  root.dataset.maInitialized = 'true';

  const sectionId = root.dataset.sectionId;
  const blockId = root.dataset.blockId;
  const handlesEl = root.querySelector('[data-ma-handles]');

  if (!sectionId || !blockId || !handlesEl) return;

  /** @type {string[]} */
  let handles = [];
  try {
    handles = JSON.parse(handlesEl.textContent || '[]');
  } catch {
    return;
  }
  if (!Array.isArray(handles) || handles.length === 0) return;

  const currencyCode = root.dataset.currencyCode || 'USD';
  const associatedProductLabel = root.dataset.associatedProductLabel || 'Associated With';

  // "Complete the look" bundle price per accessory handle, from each
  // product's custom.bundle_price metafield (plain text, e.g. "299.99").
  // Only set on products meant to show a bundle discount in this widget —
  // everything else falls back to its normal price/compare-at display.
  /** @type {Record<string, number | null>} */
  let bundlePriceCentsByHandle = {};
  const bundlePricesEl = root.querySelector('[data-ma-bundle-prices]');
  if (bundlePricesEl) {
    try {
      const raw = JSON.parse(bundlePricesEl.textContent || '{}');
      if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach((handle) => {
          const parsed = parseFloat(raw[handle]);
          bundlePriceCentsByHandle[handle] = Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
        });
      }
    } catch {
      /* ignore */
    }
  }

  // Display-name override from each product's custom.display_title metafield
  // (used for internal-only bundle-duplicate products, e.g. a real title of
  // "Amelia Foot Stool Discounted" shown to shoppers as "Amelia Foot Stool").
  // Falls back to the product's real title when unset.
  /** @type {Record<string, string>} */
  let displayTitleByHandle = {};
  const displayTitlesEl = root.querySelector('[data-ma-display-titles]');
  if (displayTitlesEl) {
    try {
      const raw = JSON.parse(displayTitlesEl.textContent || '{}');
      if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach((handle) => {
          if (typeof raw[handle] === 'string' && raw[handle]) displayTitleByHandle[handle] = raw[handle];
        });
      }
    } catch {
      /* ignore */
    }
  }

  // Discount-amount badge label per accessory handle (e.g. "50% off"), from
  // custom.discount_badge (boolean) + custom.discount_on_this_product — same
  // pair of metafields driving the "Sale" pill on regular product cards
  // (snippets/mh-product-card-badges.liquid). Omitted/null when either is unset.
  /** @type {Record<string, string>} */
  let discountLabelByHandle = {};
  const discountLabelsEl = root.querySelector('[data-ma-discount-labels]');
  if (discountLabelsEl) {
    try {
      const raw = JSON.parse(discountLabelsEl.textContent || '{}');
      if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach((handle) => {
          if (typeof raw[handle] === 'string' && raw[handle]) discountLabelByHandle[handle] = raw[handle];
        });
      }
    } catch {
      /* ignore */
    }
  }

  /** @type {HTMLFormElement | null} */
  const form = document.querySelector(
    `product-form-component[data-section-id="${CSS.escape(sectionId)}"] form`
  );
  if (!form) return;
  root.dataset.maFormId = form.id;

  const wrapperDomId = 'ma-upsell-hidden-wrapper-' + blockId;
  let hiddenWrapper = document.getElementById(wrapperDomId);
  if (!hiddenWrapper) {
    hiddenWrapper = document.createElement('div');
    hiddenWrapper.id = wrapperDomId;
    hiddenWrapper.hidden = true;
    hiddenWrapper.setAttribute('aria-hidden', 'true');
    form.appendChild(hiddenWrapper);
  }

  const track = root.querySelector('[data-track]');
  const trackDidDrag = wireMaTrackInteractions(track instanceof HTMLElement ? track : null);

  // Enlarge popup for accessory images: a native <dialog> (Escape, focus
  // containment and focus restore come from the browser), closed by the
  // close button, a click on the image/backdrop, or Escape — same behaviour
  // as the product-media zoom.
  const zoomDialog = document.createElement('dialog');
  zoomDialog.className = 'matching-accessories__zoom-dialog';

  const zoomStage = document.createElement('div');
  zoomStage.className = 'matching-accessories__zoom-stage';

  const zoomImage = document.createElement('img');
  zoomImage.className = 'matching-accessories__zoom-image';

  const zoomClose = document.createElement('button');
  zoomClose.type = 'button';
  zoomClose.className = 'matching-accessories__zoom-close';
  zoomClose.setAttribute('aria-label', 'Close');
  zoomClose.textContent = '×';

  zoomStage.appendChild(zoomImage);
  zoomDialog.appendChild(zoomStage);
  zoomDialog.appendChild(zoomClose);
  root.appendChild(zoomDialog);

  zoomClose.addEventListener('click', () => zoomDialog.close());
  zoomDialog.addEventListener('click', (e) => {
    if (e.target === zoomDialog || e.target === zoomStage || e.target === zoomImage) zoomDialog.close();
  });
  // Flush the popup on every close (button, backdrop, or Escape) so the next
  // open never shows the previous image while the new one is still loading.
  zoomDialog.addEventListener('close', () => {
    zoomImage.removeAttribute('src');
    zoomImage.alt = '';
  });

  /**
   * Requests a larger rendition of the card's current image (Shopify CDN
   * honours ?width=) and opens it in the popup.
   * @param {HTMLImageElement} cardImage
   */
  function openZoom(cardImage) {
    let largeSrc = cardImage.src;
    try {
      const url = new URL(cardImage.src);
      url.searchParams.set('width', '1200');
      largeSrc = url.toString();
    } catch {
      /* keep the card's own src */
    }
    zoomImage.src = largeSrc;
    zoomImage.alt = cardImage.alt;
    if (!zoomDialog.open) zoomDialog.showModal();
  }

  // Whole-card click toggles that card's checkbox, except when the click
  // landed on the checkbox/label itself (native toggle already handles it)
  // or on the quantity stepper, or when the click followed a drag. A click
  // on the image opens the enlarge popup instead.
  track?.addEventListener('click', (e) => {
    if (trackDidDrag()) return;
    const target = /** @type {HTMLElement} */ (e.target);
    const zoomTrigger = target.closest('.matching-accessories__media');
    if (zoomTrigger) {
      const cardImage = zoomTrigger.querySelector('.matching-accessories__image');
      if (cardImage instanceof HTMLImageElement) openZoom(cardImage);
      return;
    }
    if (target.closest('.matching-accessories__qty-stepper')) return;
    if (target.closest('.matching-accessories__checkbox-label')) return;
    const card = target.closest('.matching-accessories__card');
    if (!card) return;
    const checkbox = card.querySelector('[data-accessory-checkbox]');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /** @type {Record<string, { id: number; variants: { id: number; title: string; name?: string }[]; images: string[] }>} */
  const productData = {};

  /**
   * Swaps each rendered card's image, if the accessory product has a better
   * match than its default photo; otherwise restores the default. Cheap:
   * runs only on a fabric/colour change (or once per card right after it's
   * fetched), over a handful of already-fetched image URLs — no extra
   * network requests either way.
   *
   * Two match strategies, tried in order:
   *  1. Product-name match — e.g. on an Aaron sofa, prefer the Matching
   *     Pillow/Bolster product's "aaron-pillow.jpg" / "aaron-bolster.jpg"
   *     over any other line's photo (see maFindBestNameMatch).
   *  2. Fabric/colour-swatch match (legacy) — for any accessory that instead
   *     carries one image per colourway, matched against the currently
   *     selected swatch's data-swatch-slug.
   */
  function applyAccessoryImages() {
    const productTitleSlug = maSlugify(root.dataset.productTitle || '');
    const swatchSlug = getMaSwatchSlug();

    track?.querySelectorAll('[data-product-handle]').forEach((card) => {
      const handle = card.getAttribute('data-product-handle');
      const info = handle ? productData[handle] : null;
      const img = card.querySelector('.matching-accessories__image');
      if (!info || !(img instanceof HTMLImageElement)) return;
      const defaultSrc = img.dataset.defaultSrc || img.src;

      let match = maFindBestNameMatch(info.images, productTitleSlug);
      if (!match && swatchSlug) {
        match = info.images.find((src) => src.toLowerCase().includes(swatchSlug)) || null;
      }
      img.src = match || defaultSrc;
    });
  }

  /**
   * @param {number} cents
   */
  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  function getCurrentVariantTitle() {
    const sectionEl = document.querySelector('#shopify-section-' + CSS.escape(sectionId));
    const pickerScript = sectionEl?.querySelector('variant-picker script[type="application/json"]');
    if (pickerScript?.textContent) {
      try {
        const v = JSON.parse(pickerScript.textContent);
        if (v?.title) return /** @type {string} */ (v.title);
      } catch {
        /* ignore */
      }
    }
    return root.dataset.defaultVariantTitle || null;
  }

  // Property labels that describe something specific to the main sofa itself
  // and never make sense on a matching accessory (a pillow/bolster has no
  // "side" to it), so they're never mirrored even though everything else
  // under properties[] is copied generically.
  const MA_EXCLUDED_PROPERTY_LABELS = new Set(['chaise side']);

  /**
   * Every hidden `properties[Label]` input currently associated with the
   * main product form — fabric, colour, seat firmness, delivery lead time,
   * or any future custom-property block, excluding
   * MA_EXCLUDED_PROPERTY_LABELS — read fresh on every sync so accessories
   * always carry whatever is currently selected on the main product.
   *
   * Reads `form.elements` rather than `form.querySelectorAll(...)`: some
   * property inputs (e.g. delivery-lead-time-property.liquid) are associated
   * to the form purely via the HTML `form="..."` attribute and aren't DOM
   * descendants of the `<form>`, so a descendant-only query misses them.
   * `form.elements` includes every form-associated control regardless of
   * where it lives in the DOM.
   * @returns {{ name: string; value: string }[]}
   */
  function getMainProductProperties() {
    /** @type {{ name: string; value: string }[]} */
    const props = [];
    Array.from(form.elements).forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.type !== 'hidden') return;
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      if (input.value === '') return;
      if (MA_EXCLUDED_PROPERTY_LABELS.has(match[1].trim().toLowerCase())) return;
      props.push({ name: match[1], value: input.value });
    });
    return props;
  }

  function syncHiddenInputs() {
    if (!(hiddenWrapper instanceof HTMLDivElement)) return;
    hiddenWrapper.innerHTML = '';
    const variantTitle = getCurrentVariantTitle();
    const mainProperties = getMainProductProperties();

    root.querySelectorAll('[data-accessory-checkbox]:checked').forEach((cb) => {
      if (!(cb instanceof HTMLInputElement)) return;
      const handle = cb.dataset.productHandle;
      if (!handle) return;
      const info = productData[handle];
      if (!info || !variantTitle) return;

      let variantToUse = info.variants.find((v) => v.title === variantTitle || v.name === variantTitle);
      if (!variantToUse) variantToUse = info.variants[0];
      if (!variantToUse) return;

      const card = cb.closest('.matching-accessories__card');
      const qty = card ? Number(card.getAttribute('data-qty')) || 1 : 1;

      const inputId = document.createElement('input');
      inputId.type = 'hidden';
      inputId.name = 'items[][id]';
      inputId.value = String(variantToUse.id);

      const inputQty = document.createElement('input');
      inputQty.type = 'hidden';
      inputQty.name = 'items[][quantity]';
      inputQty.value = String(qty);

      hiddenWrapper.appendChild(inputId);
      hiddenWrapper.appendChild(inputQty);

      mainProperties.forEach((prop) => {
        const propInput = document.createElement('input');
        propInput.type = 'hidden';
        propInput.name = `items[][properties][${prop.name}]`;
        propInput.value = prop.value;
        hiddenWrapper.appendChild(propInput);
      });

      const associatedInput = document.createElement('input');
      associatedInput.type = 'hidden';
      associatedInput.name = `items[][properties][${associatedProductLabel}]`;
      associatedInput.value = root.dataset.productTitle || '';
      hiddenWrapper.appendChild(associatedInput);
    });
  }

  /** @type {{ __maSync?: () => void }} */ (root).__maSync = syncHiddenInputs;
  /** @type {{ __maApplyImages?: () => void }} */ (root).__maApplyImages = applyAccessoryImages;
  matchingAccessoriesRoots.add(root);

  root.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.matches('[data-accessory-checkbox]')) {
      syncHiddenInputs();
    }
  });

  /**
   * @param {HTMLElement} card
   * @param {number} qty
   */
  function setCardQty(card, qty) {
    const clamped = Math.max(1, qty);
    card.setAttribute('data-qty', String(clamped));
    const qtyValueEl = card.querySelector('[data-qty-value]');
    if (qtyValueEl) qtyValueEl.textContent = String(clamped);
    const checkbox = card.querySelector('[data-accessory-checkbox]');
    if (checkbox instanceof HTMLInputElement && checkbox.checked) syncHiddenInputs();
  }

  handles.forEach((handle) => {
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then((res) => res.json())
      .then((product) => {
        productData[product.handle] = {
          id: product.id,
          variants: product.variants.map(
            (/** @type {{ id: number; title: string; name?: string }} */ variant) => ({
              id: variant.id,
              title: variant.title,
              name: variant.name || variant.title,
            })
          ),
          images: Array.isArray(product.images) ? product.images : [],
        };

        // Sale price: the custom.bundle_price metafield (the "complete the
        // look" discounted price) when set, otherwise the product's normal
        // price. Strikethrough: compare_at_price takes priority when present
        // (a genuine markdown), otherwise — only when a bundle price is
        // set — the product's real price stands in as the "was" amount. No
        // bundle_price and no compare_at_price means no strikethrough.
        const bundlePriceCents = bundlePriceCentsByHandle[product.handle] ?? null;
        const saleCents = bundlePriceCents != null ? bundlePriceCents : product.price;

        let compareCents = null;
        if (product.compare_at_price && product.compare_at_price > saleCents) {
          compareCents = product.compare_at_price;
        } else if (bundlePriceCents != null && product.price > saleCents) {
          compareCents = product.price;
        }

        const price = formatMoney(saleCents);
        const compare = compareCents != null ? formatMoney(compareCents) : '';

        const imgSrc =
          typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image?.src || '';

        const displayTitle = displayTitleByHandle[product.handle] || product.title || '';

        const card = document.createElement('li');
        card.className = 'matching-accessories__card';
        card.dataset.productHandle = product.handle;
        card.setAttribute('data-qty', '1');

        const mediaButtonWrap = document.createElement('div');
        mediaButtonWrap.className = 'matching-accessories__media-wrap';

        const media = document.createElement('button');
        media.type = 'button';
        media.className = 'matching-accessories__media';
        media.setAttribute('aria-label', 'Enlarge image: ' + displayTitle);

        const img = document.createElement('img');
        img.className = 'matching-accessories__image';
        img.src = imgSrc;
        img.dataset.defaultSrc = imgSrc;
        img.alt = displayTitle;
        img.loading = 'lazy';

        media.appendChild(img);

        const label = document.createElement('label');
        label.className = 'matching-accessories__checkbox-label';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'matching-accessories__checkbox';
        checkbox.dataset.accessoryCheckbox = '';
        checkbox.dataset.productHandle = product.handle;

        const srText = document.createElement('span');
        srText.className = 'matching-accessories__checkbox-text';
        srText.textContent = root.dataset.checkboxLabel || '';

        label.appendChild(checkbox);
        label.appendChild(srText);

        mediaButtonWrap.appendChild(media);
        mediaButtonWrap.appendChild(label);

        const discountLabel = discountLabelByHandle[product.handle];
        if (discountLabel) {
          const badge = document.createElement('span');
          badge.className = 'mh-product-card__badge mh-product-card__badge--sale matching-accessories__badge';
          badge.textContent = discountLabel;
          mediaButtonWrap.appendChild(badge);
        }

        const body = document.createElement('div');
        body.className = 'matching-accessories__body';

        const titleEl = document.createElement('p');
        titleEl.className = 'matching-accessories__title';
        titleEl.textContent = displayTitle;

        const priceWrap = document.createElement('p');
        priceWrap.className = 'matching-accessories__price';

        const saleSpan = document.createElement('span');
        saleSpan.className = 'matching-accessories__price-sale';
        saleSpan.textContent = price;
        priceWrap.appendChild(saleSpan);

        if (compare) {
          const cmp = document.createElement('s');
          cmp.className = 'matching-accessories__price-compare';
          cmp.textContent = compare;
          priceWrap.appendChild(cmp);
        }

        const qtyRow = document.createElement('div');
        qtyRow.className = 'matching-accessories__qty-row';

        const qtyLabel = document.createElement('span');
        qtyLabel.className = 'matching-accessories__qty-label';
        qtyLabel.textContent = 'Qty';

        const qtyStepper = document.createElement('span');
        qtyStepper.className = 'matching-accessories__qty-stepper';

        const decBtn = document.createElement('button');
        decBtn.type = 'button';
        decBtn.className = 'matching-accessories__qty-btn';
        decBtn.setAttribute('aria-label', 'Decrease quantity');
        decBtn.textContent = '−';
        decBtn.addEventListener('click', () => setCardQty(card, (Number(card.getAttribute('data-qty')) || 1) - 1));

        const qtyValue = document.createElement('span');
        qtyValue.className = 'matching-accessories__qty-value';
        qtyValue.setAttribute('data-qty-value', '');
        qtyValue.textContent = '1';

        const incBtn = document.createElement('button');
        incBtn.type = 'button';
        incBtn.className = 'matching-accessories__qty-btn';
        incBtn.setAttribute('aria-label', 'Increase quantity');
        incBtn.textContent = '+';
        incBtn.addEventListener('click', () => setCardQty(card, (Number(card.getAttribute('data-qty')) || 1) + 1));

        qtyStepper.appendChild(decBtn);
        qtyStepper.appendChild(qtyValue);
        qtyStepper.appendChild(incBtn);

        qtyRow.appendChild(qtyLabel);
        qtyRow.appendChild(qtyStepper);

        body.appendChild(titleEl);
        body.appendChild(priceWrap);
        body.appendChild(qtyRow);

        card.appendChild(mediaButtonWrap);
        card.appendChild(body);

        track?.appendChild(card);

        applyAccessoryImages();
        syncHiddenInputs();
      })
      .catch(() => {
        /* ignore failed fetches */
      });
  });

  syncHiddenInputs();
}

function bootMatchingAccessories() {
  document.querySelectorAll('[data-matching-accessories-root]').forEach((el) => {
    if (el instanceof HTMLElement) initMatchingAccessories(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootMatchingAccessories);
} else {
  bootMatchingAccessories();
}
